import { Phone, FileText, IdCard, ShieldCheck } from "lucide-react";
import { SITE, IMAGES } from "../lib/site";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";

const steps = [
  { n: "01", title: "Chiamata & Appuntamento", text: "Ci contatti al telefono o su WhatsApp. Raccogliamo i dati del veicolo e fissiamo insieme il giorno e l'ora del ritiro, in base alle tue esigenze." },
  { n: "02", title: "Ritiro Gratuito", text: "Il nostro carroattrezzi arriva all'indirizzo indicato, in tutta Roma e provincia. Carichiamo il veicolo — funzionante o meno — senza alcun costo per te." },
  { n: "03", title: "Pratiche & Radiazione", text: "Gestiamo la radiazione al Pubblico Registro Automobilistico (PRA) e tutti gli adempimenti burocratici. Nessuna coda, nessun modulo da compilare." },
  { n: "04", title: "Certificato di Demolizione", text: "Il veicolo viene demolito in un centro autorizzato. Ti consegniamo il certificato di avvenuta demolizione: la tua responsabilità sul mezzo termina qui." },
];

const docs = [
  { icon: IdCard, title: "Documento d'identità", text: "Carta d'identità o passaporto in corso di validità dell'intestatario." },
  { icon: FileText, title: "Libretto & Carta di circolazione", text: "Quando disponibili. In caso di smarrimento ti indichiamo come procedere." },
  { icon: ShieldCheck, title: "Targhe", text: "Consegna delle targhe per la corretta radiazione. Ti guidiamo passo passo." },
];

export default function ComeFunziona() {
  useSeo({
    title: "Come Funziona la Rottamazione Auto a Roma | Ritiro & Pratiche",
    description:
      "Il processo di rottamazione a Roma in 4 passi: chiamata, ritiro gratuito, radiazione al PRA e certificato di demolizione. Documenti utili e tempi.",
    path: "/come-funziona",
  });
  return (
    <div>
      <section className="relative overflow-hidden border-b border-[#232323] pt-40 pb-20">
        <img src={IMAGES.junkRow} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" style={{ filter: "grayscale(1)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">/ Il processo</p>
          <h1 className="mt-4 font-num uppercase leading-[0.85] tracking-tight text-[13vw] md:text-[9vw]">
            <MaskedLines lines={["Come", "Funziona"]} />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-20 md:py-28">
        <div className="flex flex-col">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="group grid grid-cols-1 items-start gap-4 border-t border-[#232323] py-10 md:grid-cols-12 md:gap-8">
                <span className="font-num text-7xl md:text-9xl leading-none text-outline-red md:col-span-3">{s.n}</span>
                <h2 className="font-display text-4xl md:text-6xl tracking-wide md:col-span-5">{s.title}</h2>
                <p className="font-mono text-sm leading-relaxed text-[#8a8a8a] md:col-span-4 md:pt-2">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-[#232323] bg-[#0f0f0f] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">/ Documenti utili</p>
            <h2 className="mt-4 font-display text-5xl md:text-7xl tracking-wide">Cosa serve</h2>
            <p className="mt-4 max-w-xl font-mono text-sm text-[#8a8a8a]">Non hai tutti i documenti? Nessun problema: valutiamo ogni situazione e ti diciamo esattamente come procedere.</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-[#232323] bg-[#232323] md:grid-cols-3">
            {docs.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.title} className="bg-[#0f0f0f] p-8">
                  <Icon size={26} strokeWidth={1.5} className="text-[#ffd600]" />
                  <h3 className="mt-6 font-display text-2xl tracking-wide">{d.title}</h3>
                  <p className="mt-2 font-mono text-sm leading-relaxed text-[#8a8a8a]">{d.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12">
            <a href={`tel:${SITE.phoneTel}`} data-testid="comefunziona-call" className="inline-flex items-center gap-3 border border-[#ff3300] bg-[#ff3300] px-6 py-4 font-mono text-xs uppercase tracking-[0.16em] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#ff3300]">
              <Phone size={16} /> Parliamone: {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
